import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Upload, FileText, X, Loader2, CheckCircle, AlertCircle, ScanLine, Image as ImageIcon, FileWarning, Check, ChevronDown, ChevronUp } from 'lucide-react';

// ─── PDF.js types + loader ────────────────────────────────────────────────────
interface PDFDocumentProxy { numPages: number; getPage(n: number): Promise<PDFPageProxy>; }
interface Viewport { width: number; height: number }
interface PDFPageProxy {
  getViewport(p: { scale: number }): Viewport;
  render(p: { canvasContext: CanvasRenderingContext2D; viewport: Viewport }): { promise: Promise<void> };
}
interface PDFJsGlobal {
  getDocument(p: { data: ArrayBuffer }): { promise: Promise<PDFDocumentProxy> };
  GlobalWorkerOptions: { workerSrc: string };
}
declare global { interface Window { pdfjsLib?: PDFJsGlobal } }

let _pdfJsPromise: Promise<PDFJsGlobal> | null = null;
function getPdfJs(): Promise<PDFJsGlobal> {
  if (_pdfJsPromise) return _pdfJsPromise;
  _pdfJsPromise = new Promise<PDFJsGlobal>((resolve, reject) => {
    if (window.pdfjsLib) { resolve(window.pdfjsLib); return; }
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    s.onload = () => {
      if (!window.pdfjsLib) { reject(new Error('pdf.js nao iniciado')); return; }
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      resolve(window.pdfjsLib);
    };
    s.onerror = () => reject(new Error('Falha ao carregar pdf.js'));
    document.head.appendChild(s);
  });
  return _pdfJsPromise;
}

// ─── Image helpers ────────────────────────────────────────────────────────────
// Higher resolution = better OCR accuracy for documents like CNH/RG
const MAX_PX = 1800;
const MAX_B64_KB = 1200; // edge function checks up to 1600 KB

function canvasToJpeg(canvas: HTMLCanvasElement, quality = 0.88): string {
  return canvas.toDataURL('image/jpeg', quality).split(',')[1];
}

function resizeCanvas(src: HTMLCanvasElement, maxPx: number): HTMLCanvasElement {
  const ratio = Math.min(maxPx / src.width, maxPx / src.height, 1);
  if (ratio === 1) return src;
  const dst = document.createElement('canvas');
  dst.width  = Math.round(src.width  * ratio);
  dst.height = Math.round(src.height * ratio);
  dst.getContext('2d')!.drawImage(src, 0, 0, dst.width, dst.height);
  return dst;
}

function compressUntilFit(src: HTMLCanvasElement, startQuality = 0.88): string {
  const canvas = resizeCanvas(src, MAX_PX);
  let q = startQuality;
  while (q >= 0.5) {
    const b64 = canvasToJpeg(canvas, q);
    if (b64.length * 0.75 / 1024 < MAX_B64_KB) return b64;
    q -= 0.08;
  }
  return canvasToJpeg(canvas, 0.5);
}

async function imageFileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement('canvas');
      canvas.width  = img.width;
      canvas.height = img.height;
      canvas.getContext('2d')!.drawImage(img, 0, 0);
      resolve(compressUntilFit(canvas));
    };
    img.onerror = reject;
    img.src = url;
  });
}
async function pdfToBase64(arrayBuffer: ArrayBuffer): Promise<{ base64: string; totalPages: number }> {
  const lib = await getPdfJs();
  const pdf = await lib.getDocument({ data: arrayBuffer }).promise;
  const page = await pdf.getPage(1);
  const vp0 = page.getViewport({ scale: 1 });
  // Render at up to 3x to capture fine text detail (CNH, RG, etc.)
  const scale = Math.min(MAX_PX / vp0.width, MAX_PX / vp0.height, 3.0);
  const vp = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width  = Math.round(vp.width);
  canvas.height = Math.round(vp.height);
  await page.render({ canvasContext: canvas.getContext('2d')!, viewport: vp }).promise;
  return { base64: compressUntilFit(canvas), totalPages: pdf.numPages };
}

// ─── Edge Function ────────────────────────────────────────────────────────────
const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL as string) || '';
const SUPABASE_ANON_KEY = (import.meta.env.VITE_SUPABASE_ANON_KEY as string) || '';
const FUNCTION_URL = `${SUPABASE_URL}/functions/v1/extract-document-data`;
const FETCH_HEADERS = {
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
  'apikey': SUPABASE_ANON_KEY,
};

export async function pingEdgeFunction(): Promise<{ ok: boolean; message: string; detail: string }> {
  if (!SUPABASE_URL) return { ok: false, message: 'VITE_SUPABASE_URL nao definida', detail: 'Variavel ausente no build' };
  try {
    const res = await fetch(FUNCTION_URL, { method: 'POST', headers: FETCH_HEADERS, body: JSON.stringify({ ping: true }) });
    const rawText = await res.text().catch(() => '');
    let parsed: Record<string, unknown> = {};
    try { parsed = JSON.parse(rawText); } catch { /* ignore */ }
    if (res.ok) return { ok: true, message: String(parsed.message ?? 'OK'), detail: `HTTP ${res.status}` };
    return { ok: false, message: String(parsed.error ?? rawText.slice(0, 150)), detail: `HTTP ${res.status}` };
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    return { ok: false, message: `Falha de rede: ${msg}`, detail: 'TypeError / Failed to fetch' };
  }
}

async function callExtractFunction(fileData: string, mimeType: string, fileName: string) {
  if (!SUPABASE_URL) throw new Error('VITE_SUPABASE_URL nao definida — verifique as variaveis de ambiente do build.');
  console.log('[DocumentUploader] POST →', FUNCTION_URL, '|', fileName);
  let res: Response;
  try {
    res = await fetch(FUNCTION_URL, { method: 'POST', headers: FETCH_HEADERS, body: JSON.stringify({ fileData, mimeType, fileName }) });
  } catch (networkErr) {
    const msg = networkErr instanceof Error ? networkErr.message : String(networkErr);
    console.error('[DocumentUploader] Falha de rede:', msg);
    throw new Error(`Falha de rede (${msg}). URL: ${FUNCTION_URL}`);
  }
  const rawText = await res.text().catch(() => '');
  let parsed: Record<string, unknown> = {};
  try { parsed = rawText ? JSON.parse(rawText) : {}; } catch {
    if (!res.ok) throw new Error(`Erro HTTP ${res.status}: ${rawText.slice(0, 300)}`);
  }
  if (!res.ok) throw new Error((parsed?.error as string) ?? `Erro HTTP ${res.status}`);
  if (parsed?.error) throw new Error(parsed.error as string);
  return parsed;
}

// ─── All extractable fields ───────────────────────────────────────────────────
export type AllExtractedData = {
  // Customer info
  fullName?: string;
  cpf?: string;
  rg?: string;
  issuingAuthority?: string;
  profession?: string;
  nationality?: string;
  phone?: string;
  email?: string;
  maritalStatus?: string;
  // Installation location
  street?: string;
  addressNumber?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  utilityCompany?: string;
};

type FieldDef = { key: keyof AllExtractedData; label: string };
type FieldGroup = { id: string; label: string; fields: FieldDef[] };

const FIELD_GROUPS: FieldGroup[] = [
  {
    id: 'customer',
    label: 'Informacoes do Cliente',
    fields: [
      { key: 'fullName',        label: 'Nome completo' },
      { key: 'cpf',             label: 'CPF / CNPJ' },
      { key: 'rg',              label: 'RG' },
      { key: 'issuingAuthority',label: 'Orgao emissor' },
      { key: 'profession',      label: 'Profissao' },
      { key: 'nationality',     label: 'Nacionalidade' },
      { key: 'phone',           label: 'Telefone' },
      { key: 'email',           label: 'E-mail' },
      { key: 'maritalStatus',   label: 'Estado civil' },
    ],
  },
  {
    id: 'address',
    label: 'Local de Instalacao',
    fields: [
      { key: 'street',          label: 'Logradouro' },
      { key: 'addressNumber',   label: 'Numero' },
      { key: 'neighborhood',    label: 'Bairro' },
      { key: 'city',            label: 'Cidade' },
      { key: 'state',           label: 'Estado' },
      { key: 'zipCode',         label: 'CEP' },
      { key: 'utilityCompany',  label: 'Concessionaria' },
    ],
  },
];

const MARITAL_MAP: Record<string, string> = {
  Single: 'Solteiro(a)', Married: 'Casado(a)', Divorced: 'Divorciado(a)', Widowed: 'Viuvo(a)',
};

function formatPhone(raw: string): string {
  const d = raw.replace(/\D/g, '');
  if (d.length === 11) return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`;
  return raw;
}

function displayValue(key: keyof AllExtractedData, val: string): string {
  if (key === 'maritalStatus') return MARITAL_MAP[val] ?? val;
  return val;
}

// ─── Component types ──────────────────────────────────────────────────────────
interface FileEntry {
  id: string;
  name: string;
  mimeType: string;
  previewUrl: string | null;
  status: 'queued' | 'converting' | 'loading' | 'success' | 'error';
  error?: string;
  extractedFields: string[];
  pageCount?: number;
}

interface Props {
  onExtracted: (data: AllExtractedData) => void;
  disabled?: boolean;
  allowedGroups?: string[];
}

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
const MAX_FILE_MB = 20;

// ─── Field Picker ─────────────────────────────────────────────────────────────
interface FieldPickerProps {
  data: AllExtractedData;
  onApply: (selected: AllExtractedData) => void;
  onDismiss: () => void;
}

function FieldPicker({ data, onApply, onDismiss }: FieldPickerProps) {
  const extractedKeys = Object.keys(data).filter(k => {
    const v = data[k as keyof AllExtractedData];
    return v !== null && v !== undefined && v !== '';
  }) as (keyof AllExtractedData)[];

  const [selected, setSelected] = useState<Set<keyof AllExtractedData>>(new Set(extractedKeys));
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const toggle = (key: keyof AllExtractedData) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleGroup = (group: FieldGroup) => {
    const groupKeys = group.fields.map(f => f.key).filter(k => extractedKeys.includes(k));
    const allSelected = groupKeys.every(k => selected.has(k));
    setSelected(prev => {
      const next = new Set(prev);
      groupKeys.forEach(k => allSelected ? next.delete(k) : next.add(k));
      return next;
    });
  };

  const toggleCollapse = (id: string) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleApply = () => {
    const filtered: AllExtractedData = {};
    for (const key of selected) {
      (filtered as Record<string, unknown>)[key] = data[key];
    }
    onApply(filtered);
  };

  const groupsWithData = FIELD_GROUPS.filter(g => g.fields.some(f => extractedKeys.includes(f.key)));

  if (groupsWithData.length === 0) return null;

  return (
    <div className="border border-blue-200 rounded-xl bg-white shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-blue-50 border-b border-blue-100">
        <div className="flex items-center gap-2">
          <Check size={15} className="text-blue-600" />
          <span className="text-sm font-semibold text-blue-800">Selecione os campos a preencher</span>
          <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">
            {selected.size} de {extractedKeys.length}
          </span>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          title="Fechar"
        >
          <X size={15} />
        </button>
      </div>

      {/* Groups */}
      <div className="divide-y divide-gray-100">
        {groupsWithData.map(group => {
          const groupExtracted = group.fields.filter(f => extractedKeys.includes(f.key));
          const allGroupSelected = groupExtracted.every(f => selected.has(f.key));
          const isCollapsed = collapsed.has(group.id);

          return (
            <div key={group.id}>
              {/* Group header */}
              <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors">
                <button
                  type="button"
                  onClick={() => toggleCollapse(group.id)}
                  className="flex items-center gap-2 flex-1 text-left"
                >
                  {isCollapsed ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronUp size={14} className="text-gray-400" />}
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{group.label}</span>
                  <span className="text-xs text-gray-400">({groupExtracted.length} campos)</span>
                </button>
                <button
                  type="button"
                  onClick={() => toggleGroup(group)}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors px-2"
                >
                  {allGroupSelected ? 'Desmarcar todos' : 'Marcar todos'}
                </button>
              </div>

              {/* Fields */}
              {!isCollapsed && (
                <div className="px-4 py-2 space-y-1.5">
                  {groupExtracted.map(({ key, label }) => {
                    const val = data[key] as string;
                    const isChecked = selected.has(key);
                    return (
                      <label
                        key={key}
                        className={`flex items-start gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                          isChecked ? 'bg-blue-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggle(key)}
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer flex-shrink-0"
                        />
                        <div className="min-w-0">
                          <span className="text-xs font-medium text-gray-500 block">{label}</span>
                          <span className={`text-sm font-medium break-words ${isChecked ? 'text-gray-800' : 'text-gray-400 line-through'}`}>
                            {displayValue(key, val)}
                          </span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t border-gray-100 gap-3">
        <button
          type="button"
          onClick={() => setSelected(new Set(extractedKeys))}
          className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
        >
          Selecionar tudo
        </button>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onDismiss}
            className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={selected.size === 0}
            className="text-xs px-4 py-1.5 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Aplicar {selected.size > 0 ? `${selected.size} campo${selected.size !== 1 ? 's' : ''}` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function DocumentUploader({ onExtracted, disabled, allowedGroups }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<FileEntry[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [pingResult, setPingResult] = useState<{ ok: boolean; message: string; detail: string } | null>(null);
  const [pinging, setPinging] = useState(false);
  const [extractedPool, setExtractedPool] = useState<AllExtractedData | null>(null);
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => { getPdfJs().catch(() => {}); }, []);

  const handlePing = async () => {
    setPinging(true);
    setPingResult(null);
    const result = await pingEdgeFunction();
    setPingResult(result);
    setPinging(false);
  };

  const mergeIntoPool = (incoming: AllExtractedData) => {
    setExtractedPool(prev => {
      const merged: AllExtractedData = { ...(prev ?? {}) };
      // Only keep fields that belong to allowed groups
      const allowedKeys = allowedGroups
        ? FIELD_GROUPS.filter(g => allowedGroups.includes(g.id)).flatMap(g => g.fields.map(f => f.key))
        : (Object.keys(incoming) as (keyof AllExtractedData)[]);
      for (const key of allowedKeys) {
        const val = incoming[key as keyof AllExtractedData];
        if (val !== null && val !== undefined && val !== '' && !merged[key]) {
          (merged as Record<string, unknown>)[key] = val;
        }
      }
      return merged;
    });
    setShowPicker(true);
  };

  const processFile = useCallback(async (file: File, id: string) => {
    setFiles(prev => prev.map(f => f.id === id ? { ...f, status: 'converting' } : f));

    try {
      let fileData: string;
      let pageCount: number | undefined;

      if (file.type === 'application/pdf') {
        const buf = await file.arrayBuffer();
        const result = await pdfToBase64(buf);
        fileData = result.base64;
        pageCount = result.totalPages;
      } else {
        fileData = await imageFileToBase64(file);
      }

      setFiles(prev => prev.map(f => f.id === id ? { ...f, status: 'loading' } : f));

      const response = await callExtractFunction(fileData, 'image/jpeg', file.name);
      const raw = (response?.data ?? {}) as Record<string, unknown>;

      // Format phone if present
      if (typeof raw.phone === 'string') raw.phone = formatPhone(raw.phone);

      // Collect non-null values
      const filled: AllExtractedData = {};
      const fieldNames: string[] = [];
      for (const group of FIELD_GROUPS) {
        for (const { key, label } of group.fields) {
          const v = raw[key];
          if (v !== null && v !== undefined && v !== '') {
            (filled as Record<string, unknown>)[key] = v;
            fieldNames.push(label);
          }
        }
      }

      mergeIntoPool(filled);

      setFiles(prev =>
        prev.map(f => f.id === id ? { ...f, status: 'success', extractedFields: fieldNames, pageCount } : f)
      );
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao processar documento.';
      console.error('DocumentUploader error:', err);
      setFiles(prev => prev.map(f => f.id === id ? { ...f, status: 'error', error: msg } : f));
    }
  }, []);

  const addFiles = useCallback((newFiles: File[]) => {
    const valid: { file: File; entry: FileEntry }[] = [];
    for (const file of newFiles) {
      if (!ACCEPTED_TYPES.includes(file.type)) continue;
      if (file.size > MAX_FILE_MB * 1024 * 1024) continue;
      const id = `${Date.now()}-${Math.random()}`;
      const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
      valid.push({ file, entry: { id, name: file.name, mimeType: file.type, previewUrl, status: 'queued', extractedFields: [] } });
    }
    if (!valid.length) return;
    setFiles(prev => [...prev, ...valid.map(v => v.entry)]);
    valid.forEach(({ file, entry }) => processFile(file, entry.id));
  }, [processFile]);

  const removeFile = (id: string) => {
    setFiles(prev => {
      const f = prev.find(f => f.id === id);
      if (f?.previewUrl) URL.revokeObjectURL(f.previewUrl);
      return prev.filter(f => f.id !== id);
    });
  };

  const handleApply = (selected: AllExtractedData) => {
    onExtracted(selected);
    setShowPicker(false);
    setExtractedPool(null);
  };

  const anyLoading = files.some(f => ['loading', 'queued', 'converting'].includes(f.status));

  return (
    <div className="mb-6 space-y-3">
      <div className="flex items-center gap-2">
        <ScanLine size={18} className="text-blue-600" />
        <span className="text-sm font-medium text-gray-700">Preencher automaticamente via documento</span>
        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">IA</span>
        {anyLoading && <Loader2 size={14} className="text-blue-500 animate-spin ml-1" />}
      </div>

      {/* Drop zone */}
      <div
        onClick={() => !disabled && inputRef.current?.click()}
        onDrop={e => { e.preventDefault(); setIsDragging(false); if (!disabled) addFiles(Array.from(e.dataTransfer.files)); }}
        onDragOver={e => { e.preventDefault(); if (!disabled) setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        className={[
          'border-2 border-dashed rounded-xl px-5 py-4 flex flex-col items-center gap-1.5 transition-all',
          disabled ? 'opacity-50 cursor-not-allowed border-gray-200' : 'cursor-pointer border-blue-200 hover:border-blue-400 hover:bg-blue-50',
          isDragging ? 'border-blue-500 bg-blue-50 scale-[1.01]' : '',
        ].join(' ')}
      >
        <Upload size={24} className="text-blue-400" />
        <p className="text-sm text-gray-600 text-center">
          <span className="font-semibold text-blue-600">Clique</span> ou arraste documentos aqui
        </p>
        <p className="text-xs text-gray-400">RG, CNH, Comprovante, Contrato • PDF, JPG, PNG, WEBP • max. 20MB</p>
      </div>

      {/* Connectivity test */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handlePing}
          disabled={pinging}
          className="text-xs text-gray-400 hover:text-blue-600 underline underline-offset-2 transition-colors disabled:opacity-50"
        >
          {pinging ? 'Testando conexao...' : 'Testar conexao com IA'}
        </button>
        {pingResult && (
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
            pingResult.ok ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}>
            {pingResult.ok ? 'OK' : 'Erro'}: {pingResult.message} ({pingResult.detail})
          </span>
        )}
      </div>

      {/* File list */}
      {files.length > 0 && (
        <div className="space-y-2">
          {files.map(f => (
            <div
              key={f.id}
              className={[
                'flex items-start gap-3 rounded-xl border p-3 transition-colors',
                f.status === 'success' ? 'border-green-200 bg-green-50' : '',
                f.status === 'error'   ? 'border-red-200 bg-red-50'     : '',
                ['loading','queued','converting'].includes(f.status) ? 'border-blue-200 bg-blue-50' : '',
              ].join(' ')}
            >
              <div className="flex-shrink-0">
                {f.previewUrl ? (
                  <img src={f.previewUrl} alt={f.name} className="w-12 h-12 object-cover rounded-lg border border-gray-200" />
                ) : (
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    f.status === 'success' ? 'bg-green-100' : f.status === 'error' ? 'bg-red-100' : 'bg-blue-100'
                  }`}>
                    <FileText size={22} className={
                      f.status === 'success' ? 'text-green-600' : f.status === 'error' ? 'text-red-500' : 'text-blue-500'
                    } />
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  {['loading','queued','converting'].includes(f.status)
                    ? <Loader2 size={14} className="text-blue-500 animate-spin flex-shrink-0" />
                    : f.status === 'success'
                    ? <CheckCircle size={14} className="text-green-600 flex-shrink-0" />
                    : <AlertCircle size={14} className="text-red-500 flex-shrink-0" />
                  }
                  <span className="text-xs font-medium text-gray-700 truncate">{f.name}</span>
                  {f.mimeType === 'application/pdf' && (
                    <span className="bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0">PDF</span>
                  )}
                  {f.mimeType.startsWith('image/') && (
                    <ImageIcon size={12} className="text-gray-400 flex-shrink-0" />
                  )}
                </div>

                {f.status === 'converting' && <p className="text-xs text-blue-600 flex items-center gap-1"><FileWarning size={10} /> Convertendo...</p>}
                {f.status === 'loading'    && <p className="text-xs text-blue-600">Analisando com IA...</p>}
                {f.status === 'queued'     && <p className="text-xs text-blue-400">Aguardando...</p>}

                {f.status === 'success' && (
                  <>
                    {f.pageCount && f.pageCount > 1 && (
                      <p className="text-xs text-gray-400 mb-1">Pagina 1 de {f.pageCount} analisada</p>
                    )}
                    {f.extractedFields.length > 0 ? (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {f.extractedFields.map(label => (
                          <span key={label} className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">{label}</span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500">Nenhum dado identificado</p>
                    )}
                  </>
                )}
                {f.status === 'error' && <p className="text-xs text-red-600 break-words">{f.error}</p>}
              </div>

              <button
                type="button"
                onClick={() => removeFile(f.id)}
                disabled={['loading','converting'].includes(f.status)}
                className="flex-shrink-0 text-gray-400 hover:text-gray-600 disabled:opacity-30 transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Field picker */}
      {showPicker && extractedPool && !anyLoading && (
        <FieldPicker
          data={extractedPool}
          onApply={handleApply}
          onDismiss={() => { setShowPicker(false); setExtractedPool(null); }}
        />
      )}

      {/* Re-open picker if dismissed but data still available */}
      {!showPicker && extractedPool && !anyLoading && (
        <button
          type="button"
          onClick={() => setShowPicker(true)}
          className="w-full text-xs text-blue-600 hover:text-blue-800 border border-blue-200 hover:border-blue-400 rounded-lg py-2 transition-colors font-medium"
        >
          Revisar campos extraidos pela IA
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(',')}
        multiple
        onChange={e => { if (e.target.files) addFiles(Array.from(e.target.files)); e.target.value = ''; }}
        className="hidden"
        disabled={disabled}
      />
    </div>
  );
}
